import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EducationStep = ({ formData, errors, onChange }) => {
  const educationLevelOptions = [
    { value: 'high-school', label: 'High School Diploma' },
    { value: 'associate', label: 'Associate Degree' },
    { value: 'bachelor', label: "Bachelor\'s Degree" },
    { value: 'master', label: "Master\'s Degree" },
    { value: 'doctorate', label: 'Doctorate/PhD' }
  ];

  const fieldOfStudyOptions = [
    { value: 'arts', label: 'Arts & Humanities' },
    { value: 'business', label: 'Business & Management' },
    { value: 'engineering', label: 'Engineering & Technology' },
    { value: 'science', label: 'Natural Sciences' },
    { value: 'social', label: 'Social Sciences' },
    { value: 'medicine', label: 'Medicine & Health' },
    { value: 'law', label: 'Law & Legal Studies' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' }
  ];

  const chineseLevelOptions = [
    { value: 'none', label: 'No Knowledge' },
    { value: 'beginner', label: 'Beginner (HSK 1-2)' },
    { value: 'elementary', label: 'Elementary (HSK 3)' },
    { value: 'intermediate', label: 'Intermediate (HSK 4)' },
    { value: 'advanced', label: 'Advanced (HSK 5-6)' }
  ];

  const addEducation = () => {
    const currentEducation = formData?.educationHistory || [];
    onChange('educationHistory', [
      ...currentEducation,
      {
        id: Date.now(),
        institution: '',
        degree: '',
        fieldOfStudy: '',
        startDate: '',
        endDate: '',
        gpa: ''
      }
    ]);
  };

  const removeEducation = (id) => {
    const currentEducation = formData?.educationHistory || [];
    onChange('educationHistory', currentEducation?.filter(edu => edu?.id !== id));
  };

  const updateEducation = (id, field, value) => {
    const currentEducation = formData?.educationHistory || [];
    onChange('educationHistory', currentEducation?.map(edu =>
      edu?.id === id ? { ...edu, [field]: value } : edu
    ));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
          Educational Background
        </h2>
        <p className="text-sm md:text-base text-muted-foreground font-caption">
          Tell us about your educational qualifications and Chinese language proficiency.
        </p>
      </div>
      <div className="space-y-4 md:space-y-6">
        <Select
          label="Highest Education Level"
          options={educationLevelOptions}
          value={formData?.highestEducation || ''}
          onChange={(value) => onChange('highestEducation', value)}
          error={errors?.highestEducation}
          placeholder="Select your highest qualification"
          required
        />

        <Input
          label="Institution Name"
          type="text"
          placeholder="Name of your most recent institution"
          value={formData?.institutionName || ''}
          onChange={(e) => onChange('institutionName', e?.target?.value)}
          error={errors?.institutionName}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Field of Study"
            options={fieldOfStudyOptions}
            value={formData?.fieldOfStudy || ''}
            onChange={(value) => onChange('fieldOfStudy', value)}
            error={errors?.fieldOfStudy}
            placeholder="Select field"
            required
          />

          <Input
            label="GPA/Grade"
            type="text"
            placeholder="e.g., 3.8/4.0 or 85%"
            value={formData?.gpa || ''}
            onChange={(e) => onChange('gpa', e?.target?.value)}
            error={errors?.gpa}
            description="Enter your overall grade"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Graduation Date"
            type="date"
            value={formData?.graduationDate || ''}
            onChange={(e) => onChange('graduationDate', e?.target?.value)}
            error={errors?.graduationDate}
            description="Or expected graduation date"
            required
          />

          <Input
            label="Years of Study"
            type="number"
            placeholder="e.g., 4"
            value={formData?.yearsOfStudy || ''}
            onChange={(e) => onChange('yearsOfStudy', e?.target?.value)}
            error={errors?.yearsOfStudy}
            min="1"
            max="10"
          />
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
          Chinese Language Proficiency
        </h3>

        <div className="space-y-4 md:space-y-6">
          <Select
            label="Current Chinese Level"
            options={chineseLevelOptions}
            value={formData?.chineseLevel || ''}
            onChange={(value) => onChange('chineseLevel', value)}
            error={errors?.chineseLevel}
            placeholder="Select your current level"
            description="Based on HSK (Hanyu Shuiping Kaoshi) standard"
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label="HSK Score (if applicable)"
              type="text"
              placeholder="e.g., HSK 4 - 245/300"
              value={formData?.hskScore || ''}
              onChange={(e) => onChange('hskScore', e?.target?.value)}
              error={errors?.hskScore}
              description="Leave blank if not taken"
            />

            <Input
              label="Test Date"
              type="date"
              value={formData?.hskTestDate || ''}
              onChange={(e) => onChange('hskTestDate', e?.target?.value)}
              error={errors?.hskTestDate}
              description="Date of HSK test"
            />
          </div>

          <Input
            label="Years of Chinese Study"
            type="number"
            placeholder="e.g., 2"
            value={formData?.yearsOfChinese || ''}
            onChange={(e) => onChange('yearsOfChinese', e?.target?.value)}
            error={errors?.yearsOfChinese}
            description="Total years studying Chinese language"
            min="0"
            max="20"
          />
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Additional Education History
          </h3>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={addEducation}
          >
            Add Education
          </Button>
        </div>

        {formData?.educationHistory && formData?.educationHistory?.length > 0 && (
          <div className="space-y-4">
            {formData?.educationHistory?.map((edu, index) => (
              <div key={edu?.id} className="p-4 md:p-6 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base md:text-lg font-caption font-medium text-foreground">
                    Education #{index + 1}
                  </h4>
                  <button
                    onClick={() => removeEducation(edu?.id)}
                    className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                    aria-label="Remove education entry"
                  >
                    <Icon name="Trash2" size={18} color="var(--color-destructive)" />
                  </button>
                </div>

                <div className="space-y-4">
                  <Input
                    label="Institution"
                    type="text"
                    placeholder="Institution name"
                    value={edu?.institution}
                    onChange={(e) => updateEducation(edu?.id, 'institution', e?.target?.value)}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Degree"
                      type="text"
                      placeholder="Degree obtained"
                      value={edu?.degree}
                      onChange={(e) => updateEducation(edu?.id, 'degree', e?.target?.value)}
                    />

                    <Input
                      label="Field of Study"
                      type="text"
                      placeholder="Major/Field"
                      value={edu?.fieldOfStudy}
                      onChange={(e) => updateEducation(edu?.id, 'fieldOfStudy', e?.target?.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Start Date"
                      type="date"
                      value={edu?.startDate}
                      onChange={(e) => updateEducation(edu?.id, 'startDate', e?.target?.value)}
                    />

                    <Input
                      label="End Date"
                      type="date"
                      value={edu?.endDate}
                      onChange={(e) => updateEducation(edu?.id, 'endDate', e?.target?.value)}
                    />

                    <Input
                      label="GPA"
                      type="text"
                      placeholder="e.g., 3.5/4.0"
                      value={edu?.gpa}
                      onChange={(e) => updateEducation(edu?.id, 'gpa', e?.target?.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EducationStep;